// Command Center shell (Phase 11.1).
//
// A separate top-level layout from the Workflow Shell. The Z Brand
// reference uses a dark-mode palette with a thin top nav (Dashboard /
// Admin / Sign Out) and no sidebar — that's the launcher pattern. The
// merch'd version uses dark Chocolate Brown as the deep background +
// peach accents, matching brand without going pure black.
//
// Wraps `<Outlet />` so /command/dashboard (and future /command/admin)
// inherit the chrome.
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { FlowLockup } from "@/components/FlowLockup";
import { cn } from "@/lib/utils";

export function CommandCenterShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.roles.includes("admin") ?? false;

  return (
    <div className="min-h-screen bg-canvas text-ink">
      {/* Top nav */}
      <header className="border-b border-rule bg-white px-6 md:px-10">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6">
          <Link
            to="/command/dashboard"
            className="flex items-center gap-3"
            aria-label="Flow — Command Center home"
          >
            <FlowLockup width={64} className="text-ink" />
            <span className="hidden text-[11px] font-medium uppercase tracking-[0.12em] text-muted sm:inline">
              Command Center
            </span>
          </Link>

          <nav className="flex items-center gap-1">
            <NavLink
              to="/command/dashboard"
              className={({ isActive }) =>
                cn(
                  "rounded-md px-4 py-2 text-sm font-semibold transition-colors",
                  isActive
                    ? "bg-ink text-white"
                    : "text-ink hover:bg-canvas",
                )
              }
            >
              Dashboard
            </NavLink>
            {isAdmin && (
              <NavLink
                to="/admin/categories"
                className={({ isActive }) =>
                  cn(
                    "rounded-md px-4 py-2 text-sm font-semibold transition-colors",
                    isActive
                      ? "bg-ink text-white"
                      : "text-ink hover:bg-canvas",
                  )
                }
              >
                Admin
              </NavLink>
            )}
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="ml-2 inline-flex items-center gap-1.5 rounded-md border border-rule bg-white px-3 py-2 text-sm font-medium text-ink hover:bg-canvas"
            >
              <LogOut className="h-4 w-4" aria-hidden />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </nav>
        </div>
      </header>

      {/* Content */}
      <main className="px-6 py-10 md:px-10">
        <div className="mx-auto max-w-7xl">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
